import React, { useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { Extension } from '@tiptap/core';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import TextStyle from '@tiptap/extension-text-style';
import FontFamily from '@tiptap/extension-font-family';
import {
  FaBold, FaItalic, FaUnderline, FaStrikethrough,
  FaListUl, FaListOl, FaAlignLeft, FaAlignCenter, FaAlignRight, FaLink, FaHeading,
} from 'react-icons/fa';
import { VariablePill, EquipmentBlock, LogoBlock, contentToEditorHtml } from './tiptapVariables';
import { cleanPastedHtml } from '../../utils/cleanPastedHtml';
import { useTranslation } from '../../translations';
import SimpleSelect from '../../ui/SimpleSelect';

// Rozmiar czcionki dla zaznaczonego tekstu (TipTap v2 nie ma oficjalnego FontSize).
// Dokłada atrybut font-size do marki textStyle → <span style="font-size:…">.
const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() { return { types: ['textStyle'] }; },
  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        fontSize: {
          default: null,
          parseHTML: (el) => el.style.fontSize || null,
          renderHTML: (attrs) => (attrs.fontSize ? { style: `font-size: ${attrs.fontSize}` } : {}),
        },
      },
    }];
  },
  addCommands() {
    return {
      setFontSize: (size) => ({ chain }) => chain().setMark('textStyle', { fontSize: size }).run(),
      unsetFontSize: () => ({ chain }) => chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run(),
    };
  },
});

// Krój i rozmiary do dropdownów. Krój = pełne stacki (fallback, gdy fontu brak na serwerze).
const fontFamilies = (t) => [
  { label: t('contracts.editor.fontDefault'), value: '' },
  { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
  { label: 'Times New Roman', value: "'Times New Roman', Times, serif" },
  // Tylko kroje, które ma obraz backendu (fonts-liberation: odpowiedniki Arial i Times New
  // Roman o tych samych wymiarach). Calibri i Georgia dawały w PDF inną czcionkę niż w
  // podglądzie (test odbiorczy 11.10.2026); sekcje już nimi oznaczone dostają krój zastępczy.
];
const fontSizes = (t) => [
  { label: t('contracts.editor.fontSize'), value: '' },
  // Z jednostką, jak „Wielkość pisma” pod podglądem: sama liczba nie mówiła, co oznacza (11.10.2026).
  ...[8, 9, 10, 11, 12, 13, 14, 16, 18, 20, 22, 24, 28, 32, 36, 48].map((n) => ({ label: `${n} px`, value: `${n}px` })),
];

// Lista w pasku edytora: niższa i węższa od listy formularza, ale szeroka na tyle, żeby
// „Rozmiar czcionki” i „Times New Roman” mieściły się w całości (dawniej „Rozm…”, 11.10.2026).
const ToolSelect = styled(SimpleSelect)`
  & > button {
    min-height: 30px;
    padding: 3px var(--sp-2);
    border-width: 1px;
    border-radius: var(--r-sm);
    font-size: var(--fs-sm);
    gap: var(--sp-1);
  }
`;
const SZEROKOSC_LISTY_PASKA = 168;

// K.3 — mini-edytor RTE (TipTap) treści sekcji umowy (HYBRYDA).
// Pisze się jak Gmail/Confluence: pasek B/I/U/nagłówek/listy/wyrównanie/link,
// zmienne jako pigułki, tabela sprzętu jako chroniony blok.
// Zapis: getHTML() (HTML-string) + format:'html'. Wstawianie zmiennych = w miejscu kursora.

const Wrapper = styled.div`
  border: 1px solid var(--input-border);
  border-radius: var(--r-sm);
  overflow: hidden;
  background: var(--card-bg);
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  padding: var(--sp-2) var(--sp-2);
  border-bottom: 1px solid var(--border);
  background: var(--panel-bg);
`;

const ToolBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 30px;
  padding: 0 7px;
  border: 1px solid ${({ $active }) => ($active ? 'var(--primary)' : 'transparent')};
  border-radius: var(--r-sm);
  background: ${({ $active }) => ($active ? 'var(--hover-bg)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--primary)' : 'var(--text-muted)')};
  cursor: pointer;
  font-size: var(--fs-base);
  transition: background var(--dur-fast) var(--ease);

  &:hover { background: var(--hover-bg); }
`;

const ToolDivider = styled.span`
  width: 1px;
  background: var(--border);
  margin: 2px var(--sp-1);
`;

const EditorArea = styled.div`
  .ProseMirror {
    min-height: 120px;
    padding: var(--sp-3) var(--sp-4);
    outline: none;
    font-size: var(--fs-base);
    line-height: 1.6;
    color: var(--text);
  }
  /* Widoczny fokus pola treści: reguła wyżej zdejmuje obrys, a globalne :focus-visible z index.css
     przegrywa z nią specyficznością, więc z klawiatury nie było widać, gdzie się pisze
     (ponowny test odbiorczy 11.10.2026). Obrys do środka, bo Wrapper przycina wystające. */
  .ProseMirror:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: -2px;
  }
  .ProseMirror p { margin: 0 0 var(--sp-2); }
  .ProseMirror > *:last-child { margin-bottom: 0; }
  .ProseMirror ul, .ProseMirror ol { padding-left: 22px; margin: 0 0 var(--sp-2); }
  .ProseMirror h1, .ProseMirror h2, .ProseMirror h3 { font-weight: 700; margin: var(--sp-1) 0 var(--sp-2); }
  .ProseMirror a { color: var(--primary); text-decoration: underline; }

  /* Pigułka zmiennej — wygląda jak @wzmianka */
  .tt-variable {
    display: inline-block;
    background: var(--info-bg);
    border: 1px solid var(--info-border);
    border-radius: var(--r-sm);
    padding: 0 7px;
    margin: 0 1px;
    font-size: 0.85em;
    color: var(--info);
    font-weight: 600;
    white-space: nowrap;
    user-select: none;
  }
  .ProseMirror-selectednode.tt-variable,
  .tt-variable.ProseMirror-selectednode { outline: 2px solid var(--primary); }

  /* Chroniony blok tabeli sprzętu */
  .tt-equipment-block {
    display: block;
    background: var(--success-bg);
    border: 1px dashed var(--success-border);
    border-radius: var(--r-sm);
    padding: var(--sp-3) var(--sp-4);
    margin: var(--sp-2) 0;
    color: var(--success);
    font-size: 0.9em;
    font-weight: 600;
    text-align: center;
    user-select: none;
  }
  .tt-equipment-block.ProseMirror-selectednode { outline: 2px solid var(--success); }

  /* Chroniony blok logo firmy */
  .tt-logo-block {
    display: block;
    background: var(--info-bg);
    border: 1px dashed var(--info-border);
    border-radius: var(--r-sm);
    padding: var(--sp-3) var(--sp-4);
    margin: var(--sp-2) 0;
    color: var(--info);
    font-size: 0.9em;
    font-weight: 600;
    text-align: center;
    user-select: none;
  }
  .tt-logo-block.ProseMirror-selectednode { outline: 2px solid var(--primary); }
`;

function SectionEditor({ sectionId, value, format, labelFor, onChange, registerEditor, readOnly = false }) {
  const { t } = useTranslation();
  // Ostatnio zsynchronizowana wartość — zapobiega pętli oraz nadpisaniu pigułek
  // przy pierwszym renderze (legacy string vs znormalizowany getHTML).
  const lastSyncedRef = useRef(value);

  const extensions = useMemo(() => [
    StarterKit.configure({
      // Undo/redo trzymamy na poziomie reducera (globalny Ctrl+Z), więc wewnętrzna
      // historia TipTap jest wyłączona — inaczej double-undo.
      history: false,
    }),
    Underline,
    TextAlign.configure({ types: ['heading', 'paragraph', 'logoBlock'] }),
    Link.configure({ openOnClick: false, autolink: false }),
    TextStyle,
    FontFamily,
    FontSize,
    VariablePill.configure({ labelFor: labelFor || ((n) => `{{${n}}}`) }),
    EquipmentBlock.configure({ placeholder: t('contracts.editor.equipmentPlaceholder') }),
    LogoBlock.configure({ placeholder: t('contracts.editor.logoPlaceholder') }),
  ], [labelFor, t]);

  const editor = useEditor({
    // Konstruktor umowy dla pracownika bez uprawnień administratora jest tylko do odczytu
    // (decyzja Piotrka 11.10.2026); serwer i tak odrzuca jego zapis.
    editable: !readOnly,
    extensions,
    content: contentToEditorHtml(value, format),
    // K.7 — czyść wklejany HTML (Word i in.) zanim TipTap go sparsuje: bez mso/fontów/kolorów.
    editorProps: {
      transformPastedHTML: (html) => cleanPastedHtml(html),
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      lastSyncedRef.current = html;
      if (onChange) onChange(html);
    },
  });

  // `readOnly` zmieniony PO utworzeniu edytora (konflikt zapisu z inną kartą blokuje edycję,
  // 12.10.2026): TipTap czyta `editable` tylko przy tworzeniu, a przy odświeżeniu opcji zostawia
  // bieżący stan, więc trzeba go ustawić wprost.
  useEffect(() => {
    if (editor && !editor.isDestroyed && editor.isEditable === readOnly) editor.setEditable(!readOnly);
  }, [editor, readOnly]);

  // Rejestracja instancji edytora w rodzicu (do wstawiania zmiennych w miejscu kursora).
  useEffect(() => {
    if (!editor || !registerEditor) return undefined;
    registerEditor(sectionId, editor);
    return () => registerEditor(sectionId, null);
  }, [editor, sectionId, registerEditor]);

  // Synchronizacja zewnętrznych zmian (undo/redo, scenariusz, auto-import) → edytor.
  // Nie odpala się przy zwykłym pisaniu (value === lastSynced po onUpdate).
  useEffect(() => {
    if (!editor) return;
    if (value !== lastSyncedRef.current) {
      lastSyncedRef.current = value;
      editor.commands.setContent(contentToEditorHtml(value, format), false);
    }
  }, [value, format, editor]);

  if (!editor) {
    return <Wrapper><EditorArea style={{ minHeight: 120 }} /></Wrapper>;
  }

  const setLink = () => {
    const prev = editor.getAttributes('link').href || '';
    const url = window.prompt(t('contracts.editor.linkPrompt'), prev);
    if (url === null) return; // anulowano
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <Wrapper>
      <Toolbar>
        <ToolBtn type="button" title={t('contracts.editor.bold')} $active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}><FaBold /></ToolBtn>
        <ToolBtn type="button" title={t('contracts.editor.italic')} $active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}><FaItalic /></ToolBtn>
        <ToolBtn type="button" title={t('contracts.editor.underline')} $active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}><FaUnderline /></ToolBtn>
        <ToolBtn type="button" title={t('contracts.editor.strike')} $active={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}><FaStrikethrough /></ToolBtn>
        <ToolDivider />
        <ToolBtn type="button" title={t('contracts.editor.heading')} $active={editor.isActive('heading', { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><FaHeading /></ToolBtn>
        <ToolBtn type="button" title={t('contracts.editor.bulletList')} $active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}><FaListUl /></ToolBtn>
        <ToolBtn type="button" title={t('contracts.editor.orderedList')} $active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}><FaListOl /></ToolBtn>
        <ToolDivider />
        <ToolBtn type="button" title={t('contracts.editor.alignLeft')} $active={editor.isActive({ textAlign: 'left' })}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}><FaAlignLeft /></ToolBtn>
        <ToolBtn type="button" title={t('contracts.editor.alignCenter')} $active={editor.isActive({ textAlign: 'center' })}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}><FaAlignCenter /></ToolBtn>
        <ToolBtn type="button" title={t('contracts.editor.alignRight')} $active={editor.isActive({ textAlign: 'right' })}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}><FaAlignRight /></ToolBtn>
        <ToolDivider />
        {/* Wybór z klawiatury NIE przenosi fokusu do tekstu: dawniej każda strzałka oddawała go
            edytorowi, więc następna przesuwała już kursor w treści i dało się zrobić tylko jeden
            krok (ponowny test odbiorczy 11.10.2026). Polecenie i tak trafia w zaznaczenie, bo
            ProseMirror trzyma je w stanie edytora. Wybór myszą wraca do tekstu jak dotąd. */}
        <ToolSelect
          style={{ width: SZEROKOSC_LISTY_PASKA }}
          aria-label={t('contracts.editor.fontFamily')}
          value={editor.getAttributes('textStyle').fontFamily || ''}
          onChange={(v, meta) => {
            const lancuch = meta && meta.zKlawiatury ? editor.chain() : editor.chain().focus();
            if (v) lancuch.setFontFamily(v).run();
            else lancuch.unsetFontFamily().run();
          }}
          options={fontFamilies(t)}
        />
        <ToolSelect
          style={{ width: SZEROKOSC_LISTY_PASKA }}
          aria-label={t('contracts.editor.fontSize')}
          value={editor.getAttributes('textStyle').fontSize || ''}
          onChange={(v, meta) => {
            const lancuch = meta && meta.zKlawiatury ? editor.chain() : editor.chain().focus();
            if (v) lancuch.setFontSize(v).run();
            else lancuch.unsetFontSize().run();
          }}
          options={fontSizes(t)}
        />
        <ToolDivider />
        <ToolBtn type="button" title={t('contracts.editor.link')} $active={editor.isActive('link')}
          onClick={setLink}><FaLink /></ToolBtn>
      </Toolbar>
      <EditorArea>
        <EditorContent editor={editor} />
      </EditorArea>
    </Wrapper>
  );
}

export default SectionEditor;
